import { AlertTriangle, ShieldAlert, TrendingDown } from "lucide-react";
import { formatMoney, formatPct, formatTRY } from "@/lib/format";
import { PAYMENTS, SPLIT_KEYS } from "@/lib/orders";

const Row = ({ label, value, strong, testid }) => (
  <div className={`flex justify-between py-1 text-[13px] ${strong ? "font-semibold text-slate-900 text-base" : "text-slate-600"}`}>
    <span>{label}</span><span className="font-mono" data-testid={testid}>{value}</span>
  </div>
);

const BANK_LIST = ["Ziraat Bankası", "Garanti BBVA", "İş Bankası", "Yapı Kredi", "Akbank", "Halkbank", "VakıfBank", "QNB", "Denizbank", "TEB"];

function InstrumentInputs({ kind, v = {}, onChange }) {
  const up = (k) => (e) => onChange({ ...v, [k]: e.target.value });
  const cls = "w-full h-8 px-2 rounded border border-slate-200 text-[12px]";
  return (
    <div className="rounded border border-blue-100 bg-blue-50/40 p-2 space-y-1.5" data-testid={`qs-instr-${kind}`}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-blue-700">{kind === "cek" ? "Çek bilgileri" : "Senet bilgileri"} · portföye otomatik alınır</div>
      <div className="grid grid-cols-3 gap-1.5">
        <input placeholder="Seri no" value={v.serial_no || ""} onChange={up("serial_no")} className={cls} data-testid={`qs-instr-${kind}-serial`} />
        <input type="date" value={v.due_date || ""} onChange={up("due_date")} className={cls} title="Vade (boşsa cari vadesi)" data-testid={`qs-instr-${kind}-due`} />
        {kind === "cek" ? (
          <select value={v.bank_name || ""} onChange={up("bank_name")} className={cls} data-testid={`qs-instr-${kind}-bank`}>
            <option value="">Banka</option>{BANK_LIST.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        ) : <input placeholder="Borçlu (boşsa cari)" value={v.drawer_name || ""} onChange={up("drawer_name")} className={cls} data-testid={`qs-instr-${kind}-drawer`} />}
      </div>
    </div>
  );
}

export default function PaymentPanel({ method, setMethod, split, setSplit, quote, currency, instr = {}, setInstr }) {
  const kinds = ["cek", "senet"].filter((k) => method === k || (method === "karma" && Number(split[k]) > 0));
  return (
    <div className="panel p-3 space-y-3" data-testid="qs-payment-panel">
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">Ödeme Türü</div>
        <div className="grid grid-cols-4 gap-1">
          {Object.entries(PAYMENTS).map(([k, l]) => (
            <button key={k} type="button" onClick={() => setMethod(k)} data-testid={`qs-pay-${k}`}
              className={`h-8 text-[12px] rounded border transition-colors ${method === k ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-blue-600"}`}>{l}</button>
          ))}
        </div>
      </div>
      {method === "karma" && (
        <div className="grid grid-cols-3 gap-2" data-testid="qs-split">
          {SPLIT_KEYS.map((k) => (
            <label key={k} className="text-[11px] text-slate-500">{PAYMENTS[k]}
              <input type="number" step="0.01" min="0" value={split[k] ?? ""} onChange={(e) => setSplit({ ...split, [k]: e.target.value })} data-testid={`qs-split-${k}`}
                className="w-full h-8 px-2 rounded border border-slate-200 text-right font-mono text-[13px]" />
            </label>
          ))}
          {quote?.split_error && <div className="col-span-3 text-[12px] text-rose-700" data-testid="qs-split-error">{quote.split_error}</div>}
        </div>
      )}
      {setInstr && kinds.map((k) => <InstrumentInputs key={k} kind={k} v={instr[k]} onChange={(val) => setInstr({ ...instr, [k]: val })} />)}
      {quote && (
        <div className="border-t border-slate-200 pt-2" data-testid="qs-totals">
          <Row label="Ara Toplam" value={formatMoney(quote.subtotal, currency)} />
          <Row label="İskonto" value={formatMoney(-quote.discount_total, currency)} />
          <Row label="KDV" value={formatMoney(quote.vat_total, currency)} />
          <Row label="Genel Toplam" value={formatMoney(quote.grand_total, currency)} strong testid="qs-grand-total" />
          {currency !== "TRY" && <Row label={`TL Karşılığı (kur ${quote.fx_rate})`} value={formatTRY(quote.grand_total_try)} testid="qs-grand-total-try" />}
          {"gross_profit" in quote && <Row label="Brüt Kâr" value={formatTRY(quote.gross_profit)} testid="qs-gross-profit" />}
          {"margin_pct" in quote && <Row label="Marj" value={formatPct(quote.margin_pct)} testid="qs-margin" />}
          {quote.deferred_amount_try > 0 && <Row label="Vadeli / Çek / Senet" value={formatTRY(quote.deferred_amount_try)} testid="qs-deferred" />}
        </div>
      )}
      {quote?.risk?.message && (
        <div className="rounded border-2 border-rose-500 bg-rose-50 px-3 py-2 text-rose-800 font-bold text-sm flex items-center gap-2 animate-pulse" data-testid="qs-risk-alert">
          <ShieldAlert className="h-5 w-5" />{quote.risk.message}
        </div>
      )}
      {quote?.reasons?.some((r) => r.code === "low_margin") && (
        <div className="rounded border border-amber-400 bg-amber-50 px-3 py-2 text-amber-900 text-[13px] flex items-center gap-2" data-testid="qs-margin-alert">
          <TrendingDown className="h-4 w-4" />DÜŞÜK MARJ – {quote.reasons.find((r) => r.code === "low_margin").message}
        </div>
      )}
      {quote?.stock_warnings?.length > 0 && (
        <div className="rounded border border-amber-400 bg-amber-50 px-3 py-2 text-amber-900 text-[12px]" data-testid="qs-stock-alert">
          <div className="font-semibold flex items-center gap-1"><AlertTriangle className="h-4 w-4" />Yetersiz stok</div>
          {quote.stock_warnings.map((w) => <div key={w.product_id}>{w.message}</div>)}
        </div>
      )}
    </div>
  );
}
